import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStatusColors } from '../contexts/StatusColorsContext';
import { useDate } from '../contexts/DateContext';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';
import TopBarUnified from './TopBarUnified';
import type { TopBarAction } from './TopBar';
import { BarChart3, Box, MapPin, ChevronLeft, ChevronRight, Settings, Eye, EyeOff, LayoutGrid } from '@ui/icons';
import { authenticatedFetch } from '../services/authService';

console.log('[NavigationUnified] Moduł załadowany');

interface Region {
  id: number;
  nazwa: string;
}

interface Wezel {
  id: number;
  nazwa: string;
  region_id: number;
}

interface NavigationProps {
  selectedRegion?: number | null;
  selectedWezel?: number | null;
  selectedDate?: string;
  onRegionChange?: (regionId: number | null) => void;
  onWezelChange?: (wezelId: number | null) => void;
  onDateChange?: (date: string) => void;
  onOpenBudowy?: () => void;
  // Cards visibility props
  cardsVisibility?: Record<string, boolean>;
  onCardsVisibilityChange?: (cardKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  cardsMenuOpen?: boolean;
  onCardsMenuToggle?: () => void;
  cardsMenuRef?: React.RefObject<HTMLDivElement>;
  isCardTransitionPending?: boolean;
  cardLabels?: Record<string, string>;
}

/**
 * Navigation using unified TopBar and icons from @ui/icons
 */
const NavigationUnified: React.FC<NavigationProps> = ({ 
  selectedRegion: propSelectedRegion, 
  selectedWezel: propSelectedWezel, 
  selectedDate: propSelectedDate,
  onRegionChange,
  onWezelChange,
  onDateChange,
  onOpenBudowy,
  // Cards visibility props
  cardsVisibility = {},
  onCardsVisibilityChange,
  cardsMenuOpen = false,
  onCardsMenuToggle,
  cardsMenuRef,
  isCardTransitionPending = false,
  cardLabels = {}
}) => {
  const sc = useStatusColors();
  const { user, isAdmin, logout } = useAuth();
  const getBase = () => {
    try {
      const w: any = window as any;
      const hinted = w.__APP_BASENAME__ || w.APP_BASENAME;
      const isPlaceholder = (s: any) => typeof s === 'string' && /^\s*\$\{[^}]+\}\s*$/.test(s);
      if (typeof hinted === 'string' && hinted.length > 0 && !isPlaceholder(hinted)) return hinted;
      const p = window.location.pathname || '/';
      if (p.startsWith('/zamowienia')) return '/zamowienia';
      if (p.startsWith('/portal')) return '/portal';
      if (p.startsWith('/kontroling')) return '/kontroling';
      if (p.startsWith('/sprzedaz')) return '/sprzedaz';
      if (p.startsWith('/zarzadzanie')) return '/zarzadzanie';
      if (p.startsWith('/wezel')) return '/wezel';
      return '/';
    } catch {
      return '/';
    }
  };
  const basePath = getBase();
  const [runtimeBasePath, setRuntimeBasePath] = React.useState<string>(basePath);
  const [runtimeTenantId, setRuntimeTenantId] = React.useState<string>('');
  const effectiveBasePath = React.useMemo(() => (runtimeBasePath || basePath), [runtimeBasePath, basePath]);
  React.useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch(`${basePath.replace(/\/$/, '')}/config/runtime.json`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data && typeof data.basePath === 'string' && data.basePath) setRuntimeBasePath(data.basePath);
          if (data && typeof data.tenantId === 'string') setRuntimeTenantId(data.tenantId);
        }
      } catch {}
    })();
  }, [basePath]);
  const defaultLogo = React.useMemo(() => (effectiveBasePath.replace(/\/$/, '') + '/images/company_logo.gif'), [effectiveBasePath]);
  const tenantLogo = React.useMemo(() => (runtimeTenantId ? (effectiveBasePath.replace(/\/$/, '') + `/images/${runtimeTenantId}/company_logo.gif`) : defaultLogo), [runtimeTenantId, effectiveBasePath, defaultLogo]);
  const [logoSrc, setLogoSrc] = React.useState<string>(tenantLogo);
  React.useEffect(() => { setLogoSrc(tenantLogo); }, [tenantLogo]);
  
  const [regions, setRegions] = useState<Region[]>([]);
  const [wezels, setWezels] = useState<Wezel[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(propSelectedRegion || null);
  const [selectedWezel, setSelectedWezel] = useState<number | null>(propSelectedWezel || null);
  const [loadingWezels, setLoadingWezels] = useState(false);
  const regionsLoadedRef = useRef(false);
  const { selectedDate, setSelectedDate } = useDate();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Sync props with local state
  useEffect(() => {
    if (propSelectedRegion !== undefined) {
      setSelectedRegion(propSelectedRegion);
    }
  }, [propSelectedRegion]);

  useEffect(() => {
    if (propSelectedWezel !== undefined) {
      setSelectedWezel(propSelectedWezel);
    }
  }, [propSelectedWezel]);

  useEffect(() => {
    if (propSelectedDate !== undefined && propSelectedDate !== selectedDate) {
      setSelectedDate(propSelectedDate);
    }
  }, [propSelectedDate, selectedDate, setSelectedDate]);

  // Close cards menu when clicking outside
  useEffect(() => {
    if (!cardsMenuOpen || !onCardsMenuToggle) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (cardsMenuRef?.current && !cardsMenuRef.current.contains(event.target as Node)) {
        onCardsMenuToggle();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cardsMenuOpen, onCardsMenuToggle, cardsMenuRef]);

  // Load regions after authentication is complete (only once)
  useAuthenticatedEffect(() => {
    console.log('[NavigationUnified] useAuthenticatedEffect triggered, regionsLoadedRef.current:', regionsLoadedRef.current);
    if (!regionsLoadedRef.current) {
      regionsLoadedRef.current = true;
      console.log('[NavigationUnified] Loading regions...');
      loadRegions();
    }
  }, []);

  const loadRegions = async () => {
    try {
      const response = await authenticatedFetch('/api/auth/user-regions', { credentials: 'include' });
      
      console.log('Regions response status:', response.status);
      if (response.ok) {
        const regionsData = await response.json();
        console.log('Regions data:', regionsData);
        setRegions(regionsData.regions || []);
      } else {
        console.error('Failed to load regions:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  };

  // Load wezels when region changes (only if authenticated)
  useAuthenticatedEffect(() => {
    if (selectedRegion) {
      loadWezelsForRegion(selectedRegion);
    } else {
      setWezels([]);
    }
  }, [selectedRegion]);

  const loadWezelsForRegion = async (regionId: number) => {
    try {
      setLoadingWezels(true);
      const response = await authenticatedFetch(`/api/wezels?region_id=${regionId}`, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.wezels) {
          setWezels(data.wezels);
        }
      }
    } catch (error) {
      console.error('Error loading wezels:', error);
    } finally {
      setLoadingWezels(false);
    }
  };

  const shiftDate = (dateStr: string, days: number): string => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  // TopBar actions with unified icons - kolejność: Panele, Surowce, Statystyki, Budowy, Administracja
  // Use useMemo to ensure icons are created only once and after component mount
  const topBarActions: TopBarAction[] = useMemo(() => [
    // Panel visibility action - FIRST!
    ...(onCardsMenuToggle ? [{
      id: 'panele',
      label: 'Panele',
      icon: <LayoutGrid className="w-5 h-5" />,
      onClick: () => onCardsMenuToggle()
    }] : []),
    {
      id: 'surowce',
      label: 'Surowce',
      icon: <Box className="w-5 h-5" />,
      onClick: () => {
        try {
          const base = getBase();
          const url = (base === '/' ? '' : base) + '/surowce';
          const moduleWindow = window.open(url, '_blank');
          if (!moduleWindow) {
            console.warn('[NavigationUnified] Failed to open Surowce window - popup may be blocked');
          } else {
            console.log('[NavigationUnified] Surowce window opened, window.opener should be available');
          }
        } catch (e) { console.error('[Nav] failed to open Surowce', e); }
      }
    },
    {
      id: 'statistics',
      label: 'Statystyki',
      icon: <BarChart3 className="w-5 h-5" />,
      onClick: () => {
        try {
          const base = getBase();
          const url = (base === '/' ? '' : base) + '/statistics';
          const moduleWindow = window.open(url, '_blank');
          if (!moduleWindow) {
            console.warn('[NavigationUnified] Failed to open Statistics window - popup may be blocked');
          } else {
            console.log('[NavigationUnified] Statistics window opened, window.opener should be available');
          }
        } catch (e) { console.error('[Nav] failed to open Statistics', e); }
      }
    },
    {
      id: 'budowy',
      label: 'Budowy',
      icon: <MapPin className="w-5 h-5" />,
      onClick: () => {
        try {
          if (onOpenBudowy) { onOpenBudowy(); return; }
          const base = getBase();
          const rid = (typeof selectedRegion === 'number' && Number.isFinite(selectedRegion)) ? `?region_id=${selectedRegion}` : '';
          const url = (base === '/' ? '' : base) + '/budowy' + rid;
          const moduleWindow = window.open(url, '_blank');
          if (!moduleWindow) {
            console.warn('[NavigationUnified] Failed to open Budowy window - popup may be blocked');
          } else {
            console.log('[NavigationUnified] Budowy window opened, window.opener should be available');
          }
        } catch (e) { console.error('[Nav] failed to open Budowy', e); }
      }
    },
    // Admin-only button - na końcu
    ...(isAdmin() ? [{
      id: 'admin',
      label: 'Administracja',
      icon: <Settings className="w-5 h-5" />,
      onClick: () => {
        try {
          const base = getBase();
          const url = (base === '/' ? '' : base) + '/admin';
          const moduleWindow = window.open(url, '_blank');
          if (!moduleWindow) {
            console.warn('[NavigationUnified] Failed to open Admin window - popup may be blocked');
          } else {
            console.log('[NavigationUnified] Admin window opened, window.opener should be available');
          }
        } catch (e) { console.error('[Nav] failed to open Admin', e); }
      }
    }] : [])
  ], [selectedRegion, onOpenBudowy, onCardsMenuToggle]);

  return (
    <>
      {/* Panel visibility dropdown - positioned absolutely at left edge below TopBar */}
      {cardsMenuOpen && onCardsMenuToggle && (
        <div 
          ref={cardsMenuRef as any}
          className="absolute left-0 bg-white border border-gray-200 rounded-md shadow-lg z-[60] min-w-[220px]"
          style={{ top: '38px' }} // Below TopBar (37.6px)
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-gray-200">
            <div className="text-xs font-semibold text-gray-700">Zarządzaj widocznością Paneli</div>
          </div>
          <div className="py-1">
            {Object.keys(cardLabels).map((cardKey) => (
              <label
                key={cardKey}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={cardsVisibility[cardKey] || false}
                  onChange={onCardsVisibilityChange ? onCardsVisibilityChange(cardKey) : undefined}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isCardTransitionPending}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-xs text-gray-700 flex items-center gap-1">
                  {cardsVisibility[cardKey] ? (
                    <Eye className="w-3 h-3 text-green-600" />
                  ) : (
                    <EyeOff className="w-3 h-3 text-gray-400" />
                  )}
                  {cardLabels[cardKey]}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      
      <TopBarUnified
        logo={
          <Link to="/" className="flex items-center">
            <img
              src={logoSrc}
              alt="Company Logo"
              onError={() => { if (logoSrc !== defaultLogo) setLogoSrc(defaultLogo); }}
              className="h-10 block company-logo"
            />
          </Link>
        }
      leftContent={
        <>
          {/* Region Select */}
          <div className="flex items-center gap-2 w-fit">
            <span className="text-xs font-medium text-text inline-flex items-center h-6">Region:</span>
            <select
              value={selectedRegion || ''}
              onChange={(e) => {
                const regionId = e.target.value ? parseInt(e.target.value) : null;
                setSelectedRegion(regionId);
                setSelectedWezel(null);
                onRegionChange?.(regionId);
                onWezelChange?.(null);
              }}
              className="border border-border rounded-md text-sm font-semibold font-inherit bg-card text-text cursor-pointer h-6 px-2 w-[180px]"
            >
              <option value="">Wybierz region</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.nazwa}
                </option>
              ))}
            </select>
          </div>
        </>
      }
      centerContent={
        <>
          <span className="text-xs font-medium text-text inline-flex items-center h-6">Data:</span>
          
          {/* Previous Day Button with icon */}
          <button
            onClick={() => {
              console.log('[DateNav] Previous day clicked, current:', selectedDate);
              const newDate = shiftDate(selectedDate, -1);
              console.log('[DateNav] New date calculated:', newDate);
              
              // Only call setSelectedDate - onDateChange is redundant as it also calls setSelectedDate
              setSelectedDate(newDate);
              console.log('[DateNav] Previous day handler completed');
            }}
            className="bg-bg text-muted border border-border px-1.5 rounded-md cursor-pointer font-semibold transition-all duration-200 flex items-center justify-center min-w-8 h-6 hover:bg-accent/10 hover:text-accent"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              console.log('[DateNav] Input changed to:', e.target.value);
              setSelectedDate(e.target.value);
              console.log('[DateNav] Input change completed');
            }}
            onInput={(e) => {
              if (!e.currentTarget.value) {
                e.currentTarget.value = selectedDate;
              }
            }}
            className="border border-border rounded-md text-sm font-semibold font-inherit bg-card text-text cursor-pointer h-6 px-2"
          />
          
          {/* Next Day Button with icon */}
          <button
            onClick={() => {
              console.log('[DateNav] Next day clicked, current:', selectedDate);
              const newDate = shiftDate(selectedDate, +1);
              console.log('[DateNav] New date calculated:', newDate);
              
              // Only call setSelectedDate - onDateChange is redundant
              setSelectedDate(newDate);
              console.log('[DateNav] Next day handler completed');
            }}
            className="bg-bg text-muted border border-border px-1.5 rounded-md cursor-pointer font-semibold transition-all duration-200 flex items-center justify-center min-w-8 h-6 hover:bg-accent/10 hover:text-accent"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      }
      actions={topBarActions}
      showChangePasswordModal={showChangePasswordModal}
      onChangePasswordModalToggle={setShowChangePasswordModal}
    />
    </>
  );
};

export default NavigationUnified;

